"use client";

import { useEffect, useState } from "react";
import axios from "@/lib/api";
import toast from "react-hot-toast";
import {
  Bell,
  Mail,
  MailOpen,
  X,
  User,
  AtSign,
  MessageSquare,
  Clock,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { AlarmClock, Send } from "lucide-react";

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  message: string;
  createdAt: string;
  isRead?: boolean;
}
interface ReturnDueItem {
  _id: string;
  customer: string;
  email: string;
  phone: string;
  carName: string;
  returnDate: string; // ISO
  hoursLeft: number; // e.g. 3.5
  eligible: boolean; // button active when true (<= 4h)
  lastReturnReminderAt: string | null;
  returnReminderCount: number;
}

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="w-full max-w-md mx-4 transition-all transform bg-white rounded-lg shadow-xl">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <h3 className="ml-3 text-lg font-semibold text-red-600">{title}</h3>
          </div>

          <p className="mb-6 text-gray-600">{message}</p>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-white transition-colors bg-red-500 rounded-lg hover:bg-red-600"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationsPage = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const [dueSoon, setDueSoon] = useState<ReturnDueItem[]>([]);
  const [loadingDue, setLoadingDue] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State for confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    messageId: null as string | null,
    messageName: "",
  });

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await axios.get("/contact");
        const sorted = [...res.data].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        const messagesWithReadStatus = sorted.map((msg) => ({
          ...msg,
          isRead: msg.isRead || false,
        }));

        setMessages(messagesWithReadStatus);

        const unread = messagesWithReadStatus.filter(
          (msg) => !msg.isRead
        ).length;
        setUnreadCount(unread);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load messages.");
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  useEffect(() => {
    let timer: number | null = null;

    const loadDueSoon = async () => {
      try {
        setLoadingDue(true);
        const { data } = await axios.get<{
          status: number;
          data: ReturnDueItem[];
        }>("/notifications/returns-due?windowHours=6");
        setDueSoon(data?.data ?? []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load return reminders.");
      } finally {
        setLoadingDue(false);
      }
    };

    loadDueSoon();
    // poll every 2 minutes (adjust if you want)
    timer = window.setInterval(loadDueSoon, 120_000) as unknown as number;

    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, []);

  const markAsRead = async (messageId: string) => {
    try {
      await axios.patch(`/contact/${messageId}`, { isRead: true });

      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === messageId ? { ...msg, isRead: true } : msg
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
      toast.error("Failed to mark message as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch("/contact/mark-all-read");
      setMessages((prev) => prev.map((msg) => ({ ...msg, isRead: true })));
      setUnreadCount(0);
      toast.success("All messages marked as read");
    } catch (err) {
      console.error(err);
      toast.error("Failed to mark all messages as read");
    }
  };

  // Updated handleDelete function to use custom dialog
  const handleDelete = async (id: string) => {
    const messageToDelete = messages.find((msg) => msg._id === id);
    setConfirmDialog({
      isOpen: true,
      messageId: id,
      messageName: messageToDelete?.name || "Unknown",
    });
  };

  // Function to confirm deletion
  const confirmDelete = async () => {
    if (!confirmDialog.messageId) return;

    try {
      await axios.delete(`/contact/${confirmDialog.messageId}`);
      toast.success("Message deleted");

      setMessages((prev) =>
        prev.filter((msg) => msg._id !== confirmDialog.messageId)
      );
      setUnreadCount((prev) =>
        messages.find(
          (msg) => msg._id === confirmDialog.messageId && !msg.isRead
        )
          ? Math.max(0, prev - 1)
          : prev
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete message");
    } finally {
      // Close dialog
      setConfirmDialog({
        isOpen: false,
        messageId: null,
        messageName: "",
      });
    }
  };
  const sendReturnReminder = async (id: string) => {
    setSendingId(id);
    try {
      const { data } = await axios.post<{ status: number; msg: string }>(
        `/notifications/returns/${id}/remind`
      );
      if (data?.status === 1) {
        toast.success("Reminder email sent");
        // refresh the list to reflect reminder count & last sent time
        const res = await axios.get<{ status: number; data: ReturnDueItem[] }>(
          "/notifications/returns-due?windowHours=6"
        );
        setDueSoon(res.data?.data ?? []);
      } else {
        toast.error(data?.msg || "Failed to send reminder");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to send reminder");
    } finally {
      setSendingId(null);
    }
  };

  // Function to cancel deletion
  const cancelDelete = () => {
    setConfirmDialog({
      isOpen: false,
      messageId: null,
      messageName: "",
    });
  };

  const openMessageModal = async (message: ContactMessage) => {
    setSelectedMessage(message);
    setIsModalOpen(true);

    if (!message.isRead) {
      await markAsRead(message._id);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    return message.length > maxLength
      ? message.substring(0, maxLength) + "..."
      : message;
  };

  return (
    <div className="max-w-5xl px-6 py-10 mx-auto bg-white rounded shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="w-8 h-8 text-blue-900" />
          <h1 className="text-3xl font-bold text-blue-900">Notifications</h1>
          {unreadCount > 0 && (
            <span className="px-3 py-1 text-sm font-bold text-white bg-red-500 rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>

        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="px-4 py-2 text-sm font-medium text-white transition-colors bg-blue-600 rounded hover:bg-blue-700"
          >
            Mark All as Read
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-gray-600">Loading messages...</p>
      ) : messages.length === 0 ? (
        <p className="text-gray-600">No notifications found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse table-auto">
            <thead className="bg-blue-100">
              <tr>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Message Preview</th>
                <th className="px-4 py-2 text-left">Time</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((msg) => (
                <tr
                  key={msg._id}
                  onClick={() => openMessageModal(msg)}
                  className={`transition border-b hover:bg-blue-50 cursor-pointer ${
                    !msg.isRead ? "bg-blue-25 border-l-4 border-l-blue-500" : ""
                  }`}
                >
                  <td className="px-4 py-2">
                    {msg.isRead ? (
                      <MailOpen className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Mail className="w-4 h-4 text-blue-600" />
                    )}
                  </td>
                  <td
                    className={`px-4 py-2 ${
                      !msg.isRead ? "font-bold" : "font-medium"
                    }`}
                  >
                    {msg.name}
                  </td>
                  <td className="px-4 py-2 text-blue-800">{msg.email}</td>
                  <td
                    className={`px-4 py-2 ${!msg.isRead ? "font-medium" : ""}`}
                  >
                    {truncateMessage(msg.message)}
                  </td>
                  <td className="flex items-center gap-3 px-4 py-2 text-gray-500">
                    {new Date(msg.createdAt).toLocaleString()}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(msg._id);
                      }}
                      className="p-1 text-red-500 transition hover:text-red-500"
                      title="Delete message"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="p-4 mt-6 rounded bg-gray-50">
        <p className="text-sm text-gray-600">
          Total: {messages.length} messages | Unread:{" "}
          <span className="font-semibold text-red-600">{unreadCount}</span> |
          Read:{" "}
          <span className="font-semibold text-green-600">
            {messages.length - unreadCount}
          </span>
        </p>
      </div>
      {/* ===== New: Returns Due Soon ===== */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlarmClock className="w-6 h-6 text-blue-900" />
            <h2 className="text-xl font-semibold text-blue-900">
              Returns Due Soon
            </h2>
          </div>
          <span className="text-sm text-gray-600">
            Showing vehicles due within the next{" "}
            <span className="font-medium">6 hours</span>
          </span>
        </div>

        {loadingDue ? (
          <p className="text-gray-600">Loading…</p>
        ) : dueSoon.length === 0 ? (
          <div className="p-4 text-sm text-gray-600 border rounded bg-gray-50">
            No vehicles are due for return soon.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse table-auto">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-4 py-2 text-left">Car</th>
                  <th className="px-4 py-2 text-left">Customer</th>
                  <th className="px-4 py-2 text-left">Contact</th>
                  <th className="px-4 py-2 text-left">Return At</th>
                  <th className="px-4 py-2 text-left">Hours Left</th>
                  <th className="px-4 py-2 text-left">Reminders</th>
                  <th className="px-4 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {dueSoon.map((d) => (
                  <tr
                    key={d._id}
                    className="transition border-b hover:bg-blue-50"
                  >
                    <td className="px-4 py-2">{d.carName}</td>
                    <td className="px-4 py-2">{d.customer}</td>
                    <td className="px-4 py-2">
                      <div>{d.email || "—"}</div>
                      <div className="text-xs text-gray-500">
                        {d.phone || ""}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {new Date(d.returnDate).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">{d.hoursLeft}</td>
                    <td className="px-4 py-2">
                      {d.returnReminderCount}
                      {d.lastReturnReminderAt && (
                        <span className="ml-1 text-xs text-gray-500">
                          (last{" "}
                          {new Date(d.lastReturnReminderAt).toLocaleString()})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => sendReturnReminder(d._id)}
                        disabled={!d.eligible || sendingId === d._id}
                        className={`inline-flex items-center gap-2 px-3 py-1 text-xs rounded ${
                          !d.eligible || sendingId === d._id
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-500"
                        }`}
                        title={
                          !d.eligible
                            ? "Button activates within 4 hours of return"
                            : ""
                        }
                      >
                        <Send className="w-3.5 h-3.5" />
                        {sendingId === d._id ? "Sending…" : "Send Reminder"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Custom Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Message"
        message={`Are you sure you want to delete the message from ${confirmDialog.messageName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {isModalOpen && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-md max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-600">
                Message Details
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 transition-colors rounded-full hover:text-gray-500 hover:bg-gray-100"
              >
                <X className="w-3 h-3" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="p-4 space-y-3 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">From:</span>
                  <span className="font-semibold text-gray-900">
                    {selectedMessage.name}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <AtSign className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">Email:</span>
                  <a
                    href={`mailto:${selectedMessage.email}`}
                    className="font-medium text-blue-600 hover:text-blue-800"
                  >
                    {selectedMessage.email}
                  </a>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">Received:</span>
                  <span className="text-gray-900">
                    {new Date(selectedMessage.createdAt).toLocaleString(
                      "en-US",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </span>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-600">
                    Message:
                  </span>
                </div>
                <div className="p-4 bg-white border border-gray-200 rounded-lg">
                  <p className="leading-relaxed text-gray-900 whitespace-pre-wrap">
                    {selectedMessage.message}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                {selectedMessage.isRead ? (
                  <>
                    <MailOpen className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">
                      Message marked as read
                    </span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">
                      Marking as read...
                    </span>
                  </>
                )}
              </div>

              <div className="flex gap-3">
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: Your message&body=Hi ${selectedMessage.name},%0A%0A`}
                  className="px-4 py-2 text-sm font-medium text-white transition-colors bg-blue-600 rounded hover:bg-blue-700"
                >
                  Reply via Email
                </a>
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-200 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
